import moment from 'moment';
import { RATING_MAX, RATING_MIN } from './crm.constants';
import { ClientCrmField, CrmFieldTypeEnum, ValidationDTO } from './types';

type CrmValue = string | number | boolean | string[] | null;

// false is an answer, so it must not read as empty.
const isEmpty = (val: CrmValue) =>
  val == null || (typeof val === 'string' && val.trim() === '') || (Array.isArray(val) && val.length === 0);

export const isFilled = (val: CrmValue) =>
  Array.isArray(val) ? val.length > 0 : typeof val === 'string' ? val.trim().length > 0 : val != null;

export const isValidInterval = (min?: number, max?: number) =>
  !((min ?? 0) < 0 || (max ?? 0) < 0 || (min && max && min > max));

export const parseField = (field: ClientCrmField): ClientCrmField => {
  switch (field.validation.type) {
    case CrmFieldTypeEnum.DATE:
      return {
        ...field,
        validation: {
          type: field.validation.type,
          isRequired: field.validation.isRequired,
          minDate: field.validation.minDate ?? undefined,
          maxDate: field.validation.maxDate ?? undefined,
        },
      };
    case CrmFieldTypeEnum.NUMBER:
      return {
        ...field,
        validation: {
          type: field.validation.type,
          isRequired: field.validation.isRequired,
          min: field.validation.min ?? undefined,
          max: field.validation.max ?? undefined,
        },
      };
    case CrmFieldTypeEnum.SHORT_TEXT:
      return {
        ...field,
        validation: {
          type: field.validation.type,
          isRequired: field.validation.isRequired,
          minLength: field.validation.minLength ?? undefined,
          maxLength: field.validation.maxLength ?? undefined,
        },
      };
    case CrmFieldTypeEnum.SELECT:
      return {
        ...field,
        validation: {
          type: field.validation.type,
          isRequired: field.validation.isRequired,
          minSelection:
            field.validation.minSelection && field.validation.isMultiSelect ? field.validation.minSelection : undefined,
          maxSelection:
            field.validation.maxSelection && field.validation.isMultiSelect ? field.validation.maxSelection : undefined,
          isMultiSelect: field.validation.isMultiSelect ?? undefined,
          options: field.validation.options ?? [],
        },
      };
    case CrmFieldTypeEnum.BOOL:
    case CrmFieldTypeEnum.RATING:
      return {
        ...field,
        validation: {
          type: field.validation.type,
          isRequired: field.validation.isRequired,
        },
      };
    case CrmFieldTypeEnum.USER_ZIPCODE:
      return {
        ...field,
        validation: {
          type: field.validation.type,
          isRequired: field.validation.isRequired,
        },
      };
    case CrmFieldTypeEnum.USER_LASTNAME:
      return {
        ...field,
        validation: {
          type: field.validation.type,
          isRequired: field.validation.isRequired,
        },
      };
    case CrmFieldTypeEnum.USER_FIRSTNAME:
      return {
        ...field,
        validation: {
          type: field.validation.type,
          isRequired: field.validation.isRequired,
        },
      };
    case CrmFieldTypeEnum.USER_BIRTHDAY:
      return {
        ...field,
        validation: {
          type: field.validation.type,
          isRequired: field.validation.isRequired,
        },
      };
    default:
      throw new Error('Unknown field type');
  }
};

export const handleCRMFieldError = (validation: ValidationDTO, value: CrmValue): string | undefined => {
  if (validation.isRequired && isEmpty(value)) {
    return 'crm.errors.required';
  }

  if (value == null) return;

  switch (validation.type) {
    case CrmFieldTypeEnum.SHORT_TEXT:
      return validateShortText(validation, value);

    case CrmFieldTypeEnum.NUMBER:
      return validateNumber(validation, value);

    case CrmFieldTypeEnum.DATE:
      return validateDate(validation, value);

    case CrmFieldTypeEnum.SELECT:
      return validateSelect(validation, value);

    case CrmFieldTypeEnum.BOOL:
      return validateBool(value);

    case CrmFieldTypeEnum.RATING:
      return validateRating(value);

    default:
      return 'crm.errors.unknown';
  }
};

const validateBool = (value: unknown) => {
  if (typeof value !== 'boolean') return 'crm.errors.bool.invalid';
};

const validateRating = (value: unknown) => {
  // A bool would pass Number.isInteger through Number(), so it is refused first.
  if (typeof value === 'boolean') return 'crm.errors.rating.invalid';

  const rating = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(rating)) return 'crm.errors.rating.invalid';
  if (rating < RATING_MIN || rating > RATING_MAX) return 'crm.errors.rating.range';
};

const validateShortText = (validation: ValidationDTO, value: unknown) => {
  if (typeof value !== 'string') return 'crm.errors.text.invalid';

  if (validation.minLength && value.length < validation.minLength) {
    return 'crm.errors.text.min';
  }
  if (validation.maxLength && value.length > validation.maxLength) {
    return 'crm.errors.text.max';
  }
};

const validateNumber = (validation: ValidationDTO, value: unknown) => {
  let num: number;
  if (typeof value === 'number') {
    num = value;
  } else if (typeof value === 'string' && value.trim() === '') {
    return 'crm.errors.number.invalid';
  } else if (typeof value === 'string') {
    num = parseInt(value, 10);
  } else {
    return 'crm.errors.number.invalid';
  }

  if (Number.isNaN(num)) return 'crm.errors.number.invalid';

  if (validation.min != null && num < validation.min) {
    return 'crm.errors.number.min';
  }
  if (validation.max != null && num > validation.max) {
    return 'crm.errors.number.max';
  }
};

const validateDate = (validation: ValidationDTO, value: unknown) => {
  const valueDate = moment(value as string);
  if (!valueDate.isValid()) return 'crm.errors.date.invalid';

  if (validation.minDate && valueDate.isBefore(moment(validation.minDate))) {
    return 'crm.errors.date.min';
  }
  if (validation.maxDate && valueDate.isAfter(moment(validation.maxDate))) {
    return 'crm.errors.date.max';
  }
};

const validateSelect = (validation: ValidationDTO, value: unknown) => {
  if (!Array.isArray(value)) return 'crm.errors.select.invalid';

  const { length } = value;

  if (length === 0) {
    return validation.isRequired ? 'crm.errors.required' : undefined;
  }

  if (validation.isMultiSelect) {
    if (validation.minSelection && length < validation.minSelection) {
      return 'crm.errors.select.min';
    }
    if (validation.maxSelection && length > validation.maxSelection) {
      return 'crm.errors.select.max';
    }
  } else if (length > 1) {
    return 'crm.errors.select.single';
  }
};
